/**
 * CommsView — the Mobile Forensics workspace: a reconstructed messenger over
 * the device evidence. Pure presentation; the page container owns data.
 *
 * Layout: an evidence bar naming the extraction (device, id, per-app parse
 * outcome), a tab strip (Chats / Web history), then the active surface — the
 * two-pane messenger, or the browsing timeline.
 */

import { BellIcon, GlobeIcon, MessageSquareIcon, SmartphoneIcon } from 'lucide-react'
import { type FC, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type {
  CommsActivityBucket,
  CommsAppReport,
  CommsConversation,
  CommsExcerpt,
  CommsExtraction,
  CommsMessage,
  CommsNotificationEvent,
  CommsRange,
  CommsWebVisit,
} from '@/services/api/comms'
import { cn } from '@/utils/cn'
import { ActivityStrip } from './ActivityStrip'
import { ChatThread, type ExcerptRange } from './ChatThread'
import { ConversationList } from './ConversationList'
import { appLabel, isRanged } from './comms-format'
import { MessageDetail } from './MessageDetail'
import { NotificationsList } from './NotificationsList'
import { RangeBar } from './RangeBar'
import { WebHistoryList } from './WebHistoryList'

export type CommsTab = 'chats' | 'web' | 'notifications'

/** The Notifications tab's data + paging, grouped so the view's prop list
 * doesn't grow by six flat fields per tab. */
export interface CommsNotificationsProps {
  events: CommsNotificationEvent[]
  registered: string[]
  apps: string[]
  hasMore: boolean
  loadingMore: boolean
  onLoadMore: () => void
}

export interface CommsViewProps {
  extractions: CommsExtraction[]
  activeExtractionId: string | null
  onSelectExtraction: (extractionId: string) => void
  tab: CommsTab
  onTab: (tab: CommsTab) => void
  conversations: CommsConversation[]
  activeConversationId: string | null
  onSelectConversation: (conversationId: string) => void
  messages: CommsMessage[]
  hasOlder: boolean
  loadingOlder: boolean
  onLoadOlder: () => void
  /** Promote a contiguous message range into the graph (ADR-0024). */
  onPromote?: (range: ExcerptRange) => void
  /** Publish several ranges at once — an over-cap selection split by sitting. */
  onPromoteMany?: (ranges: ExcerptRange[]) => void
  promoting?: boolean
  /** False when the extraction's tier carries no message bodies (SC-013). */
  promotable?: boolean
  notPromotableReason?: string
  /** Ranges already promoted from the open thread. */
  excerpts?: CommsExcerpt[]
  onMine?: (excerpt: CommsExcerpt) => void
  mining?: boolean
  /** The promoted range the thread was opened on (from the graph). */
  focusExcerptId?: string | null
  /** Time window the whole view is scoped to, and the open thread's histogram. */
  range: CommsRange
  onRange: (range: CommsRange) => void
  activity: CommsActivityBucket[]
  /** One search string drives the thread filter and the full-text query. */
  searchQuery: string
  onSearchQuery: (query: string) => void
  /** Word-by-word matching: tolerates typos and declensions, admits some noise. */
  fuzzy: boolean
  onFuzzy: (fuzzy: boolean) => void
  messageMatches: CommsMessage[]
  matchesHasMore: boolean
  matchesLoading: boolean
  onSelectMatch: (message: CommsMessage) => void
  webVisits: CommsWebVisit[]
  webBrowsers: string[]
  webHasMore: boolean
  webLoadingMore: boolean
  onWebLoadMore: () => void
  notifications: CommsNotificationsProps
}

/** Phone only: with a thread open under a time window, the bar keeps just the
 * window (see `data-compact` on the wrapper in CommsView). */
const HIDE_WHEN_COMPACT = 'max-md:group-data-[compact]/bar:hidden'

/** How many apps could not be shown, and — on hover, or on tap where there is
 * no hover — why. "No messages" must never read as "did not communicate". */
const NotParsedBadge: FC<{ failed: CommsAppReport[] }> = ({ failed }) => {
  const { t } = useTranslation()
  const reasons = failed.map(entry => (
    <p key={entry.app} className="text-xs">
      <span className="font-medium">{appLabel(entry.app, t)}</span>: {entry.detail ?? entry.status}
    </p>
  ))
  return (
    <Popover>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Badge
                variant="warning"
                className={cn(
                  'cursor-default text-2xs max-md:order-1 max-md:shrink-0',
                  HIDE_WHEN_COMPACT
                )}
              >
                {t('comms.evidence.notParsed', { count: failed.length })}
              </Badge>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="end" className="max-w-72">
            {reasons}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {/* A touch screen has no hover: below md a tap opens the same reasons.
          Hidden from md, where the tooltip already answers. */}
      <PopoverContent align="end" className="gap-1 md:hidden">
        {reasons}
      </PopoverContent>
    </Popover>
  )
}

/** One line of provenance: which device, which extraction, what parsed. */
const EvidenceBar: FC<{
  extractions: CommsExtraction[]
  activeId: string | null
  onSelect: (id: string) => void
  range: CommsRange
  onRange: (range: CommsRange) => void
}> = ({ extractions, activeId, onSelect, range, onRange }) => {
  const { t } = useTranslation()
  const active = extractions.find(e => e.extractionId === activeId)
  const failed = active?.report.filter(r => r.status !== 'parsed') ?? []
  const ranged = isRanged(range)
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2 md:px-4">
      <SmartphoneIcon className={cn('size-4 shrink-0 text-muted-foreground', HIDE_WHEN_COMPACT)} />
      {extractions.length > 1 ? (
        <Select value={activeId ?? undefined} onValueChange={onSelect}>
          <SelectTrigger
            className={cn(
              'h-7 w-auto min-w-0 text-xs max-md:flex-1 max-md:basis-1/2 md:min-w-44',
              HIDE_WHEN_COMPACT
            )}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {extractions.map(extraction => (
              <SelectItem key={extraction.extractionId} value={extraction.extractionId}>
                {extraction.device.device_name ?? extraction.extractionId}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <span
          className={cn(
            'text-sm font-medium text-foreground max-md:min-w-0 max-md:flex-1 max-md:truncate',
            HIDE_WHEN_COMPACT
          )}
        >
          {active?.device.device_name ?? active?.extractionId}
        </span>
      )}
      {/* Phone: three rows, set by `order` — device + parse outcome, then the
          identifiers, then the time window. From md this wrapper dissolves
          and everything sits on the one line. */}
      <span
        className={cn(
          'flex flex-wrap items-center gap-x-2 max-md:order-2 max-md:w-full md:contents',
          HIDE_WHEN_COMPACT
        )}
      >
        {active?.device.phone_number && (
          <span className="font-mono text-2xs text-muted-foreground">
            {active.device.phone_number}
          </span>
        )}
        <span className="font-mono text-2xs text-muted-foreground">
          {t('comms.evidence.extraction', { id: active?.extractionId })}
        </span>
      </span>
      <span className="min-w-0 flex-1 max-md:hidden" />
      {failed.length > 0 && <NotParsedBadge failed={failed} />}
      <RangeBar range={range} onRange={onRange} />
      {/* Phone: closes the time-window row (the page summary that says
          read-only is not shown there). Dropped once a range is set — the row
          is full then, and the badge would take a line of its own. */}
      <Badge
        variant="outline"
        className={cn(
          'font-mono text-2xs max-md:order-3 max-md:ml-auto',
          ranged && 'max-md:hidden'
        )}
      >
        {t('comms.evidence.readOnly')}
      </Badge>
    </div>
  )
}

const PickConversation: FC = () => {
  const { t } = useTranslation()
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
      <MessageSquareIcon className="size-8 opacity-40" />
      <p className="text-sm">{t('comms.thread.pick')}</p>
    </div>
  )
}

const TabStrip: FC<{ tab: CommsTab; onTab: (t: CommsTab) => void }> = ({ tab, onTab }) => {
  const { t } = useTranslation()
  const item = (key: CommsTab, Icon: typeof MessageSquareIcon, label: string) => (
    <button
      type="button"
      onClick={() => onTab(key)}
      aria-current={tab === key || undefined}
      className={cn(
        '-mb-px inline-flex items-center gap-1.5 border-b-2 px-1 py-2.5 font-medium text-xs transition-colors max-md:flex-1 max-md:justify-center md:px-3 md:py-2',
        tab === key
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      )}
    >
      <Icon className="size-3.5" />
      {label}
    </button>
  )
  return (
    <div className={cn('flex items-center gap-1 border-border border-b px-2', HIDE_WHEN_COMPACT)}>
      {item('chats', MessageSquareIcon, t('comms.tabs.chats'))}
      {item('web', GlobeIcon, t('comms.tabs.web'))}
      {item('notifications', BellIcon, t('comms.tabs.notifications'))}
    </div>
  )
}

export const CommsView: FC<CommsViewProps> = props => {
  const activeConversation =
    props.conversations.find(c => c.conversationId === props.activeConversationId) ?? null

  // Which message's provenance panel is open. Held by id, not by value, so a
  // page of older messages loading in cannot resurrect a stale copy of the row.
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selectedMessage = props.messages.find(m => m.messageId === selectedId) ?? null
  // Below lg the panel takes the thread's place, so a search hit must not open
  // it: the analyst asked for the message and has to land on it, highlighted
  // in its thread. The panel waits (CSS only — from lg it shows as ever) until
  // a bubble is tapped.
  const [panelDeferred, setPanelDeferred] = useState(false)
  const detailRef = useRef<HTMLDivElement>(null)
  const selectBubble = (message: CommsMessage) => {
    // A deferred panel is display:none below lg: there a tap on the highlighted
    // hit opens it instead of deselecting the message.
    const panelHidden = panelDeferred && detailRef.current?.offsetParent === null
    setPanelDeferred(false)
    setSelectedId(current =>
      current === message.messageId && !panelHidden ? null : message.messageId
    )
  }

  // Phone only (below md the panes show one at a time): an open thread takes
  // the whole view. Going back records which thread the list was reopened
  // over, so opening any other one — a row, a search hit, a deep link — shows
  // it again without an effect to reset anything.
  const [listOverId, setListOverId] = useState<string | null>(null)
  const threadOpen = !!activeConversation && listOverId !== activeConversation.conversationId
  // …where it gives the evidence bar's rows to the messages — unless a time
  // window is in effect, which has to stay visible (and clearable) while the
  // thread it filters is on screen.
  const ranged = isRanged(props.range)
  const threadOnPhone = props.tab === 'chats' && threadOpen
  const barHiddenOnPhone = threadOnPhone && !ranged

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden border-t border-border bg-background md:rounded-lg md:border">
      <div
        className={cn('group/bar shrink-0', barHiddenOnPhone && 'max-md:hidden')}
        data-compact={(threadOnPhone && ranged) || undefined}
      >
        <EvidenceBar
          extractions={props.extractions}
          activeId={props.activeExtractionId}
          onSelect={props.onSelectExtraction}
          range={props.range}
          onRange={props.onRange}
        />
        <TabStrip tab={props.tab} onTab={props.onTab} />
      </div>
      {props.tab === 'chats' ? (
        <div
          className={cn(
            'grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[minmax(16rem,22rem)_minmax(0,1fr)]',
            selectedMessage &&
              'lg:grid-cols-[minmax(16rem,22rem)_minmax(0,1fr)_minmax(15rem,20rem)]'
          )}
        >
          <div
            className={cn(
              'col-start-1 row-start-1 min-h-0 border-border md:border-r',
              threadOpen && 'max-md:hidden'
            )}
          >
            {/* Keyed by extraction so search + app filter reset with the device. */}
            <ConversationList
              key={props.activeExtractionId ?? 'none'}
              conversations={props.conversations}
              activeId={props.activeConversationId}
              onSelect={id => {
                // The open panel belongs to the thread being left.
                setSelectedId(null)
                setListOverId(null)
                props.onSelectConversation(id)
              }}
              query={props.searchQuery}
              onQueryChange={props.onSearchQuery}
              fuzzy={props.fuzzy}
              onFuzzy={props.onFuzzy}
              matches={props.messageMatches}
              matchesHasMore={props.matchesHasMore}
              matchesLoading={props.matchesLoading}
              onSelectMatch={message => {
                setSelectedId(message.messageId)
                setPanelDeferred(true)
                setListOverId(null)
                props.onSelectMatch(message)
              }}
            />
          </div>
          {/* Below lg the provenance panel takes the thread's place instead of
              squeezing in as a third column: it sits in the thread's grid cell,
              over it. The thread underneath is hidden from view but stays laid
              out, so its scroll position (land at the newest message, the
              activity strip's newest day) is right when the panel closes. */}
          <div
            className={cn(
              'col-start-1 row-start-1 min-h-0 md:col-start-2',
              !threadOpen && 'max-md:hidden',
              selectedMessage && !panelDeferred && 'max-lg:invisible'
            )}
          >
            {activeConversation ? (
              <div className="flex h-full min-h-0 flex-col">
                <ActivityStrip
                  buckets={props.activity}
                  onPick={(from, to) => props.onRange({ from, to })}
                />
                <div className="min-h-0 flex-1">
                  <ChatThread
                    conversation={activeConversation}
                    onBack={() => setListOverId(activeConversation.conversationId)}
                    messages={props.messages}
                    hasOlder={props.hasOlder}
                    loadingOlder={props.loadingOlder}
                    onLoadOlder={props.onLoadOlder}
                    selectedId={selectedId}
                    onSelect={selectBubble}
                    onPromote={props.onPromote}
                    onPromoteMany={props.onPromoteMany}
                    promoting={props.promoting}
                    promotable={props.promotable}
                    notPromotableReason={props.notPromotableReason}
                    excerpts={props.excerpts}
                    onMine={props.onMine}
                    mining={props.mining}
                    focusExcerptId={props.focusExcerptId}
                  />
                </div>
              </div>
            ) : (
              <PickConversation />
            )}
          </div>
          {selectedMessage && (
            <div
              ref={detailRef}
              className={cn(
                'col-start-1 row-start-1 min-h-0 border-border max-lg:bg-background md:col-start-2 lg:col-start-3 lg:border-l',
                panelDeferred && 'max-lg:hidden'
              )}
            >
              <MessageDetail message={selectedMessage} onClose={() => setSelectedId(null)} />
            </div>
          )}
        </div>
      ) : props.tab === 'web' ? (
        <div className="min-h-0 flex-1">
          {/* Keyed by extraction so search + browser filter reset with the device. */}
          <WebHistoryList
            key={props.activeExtractionId ?? 'none'}
            visits={props.webVisits}
            browsers={props.webBrowsers}
            hasMore={props.webHasMore}
            loadingMore={props.webLoadingMore}
            onLoadMore={props.onWebLoadMore}
          />
        </div>
      ) : (
        <div className="min-h-0 flex-1">
          {/* Keyed by extraction so search + app filter reset with the device. */}
          <NotificationsList key={props.activeExtractionId ?? 'none'} {...props.notifications} />
        </div>
      )}
    </div>
  )
}
